import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { jwtDecode } from 'jwt-decode'
import { JWTPayload, useUserV1, UserV1 } from '@tovala/browser-apis-combinedapi'

import { identifyUser, identifyUserFromJWT } from 'utils/analytics'
import { identifyCIOUser, resetCIOUser } from 'utils/customerio'
import { isDateBefore } from 'utils/dates'
import {
  clearPendingRecommendedMealFilters,
  setPendingRecommendedMealFilters,
} from 'utils/menus'
import {
  getCookie,
  getTovalaCookieOptions,
  removeCookie,
  setCookie,
} from 'utils/storage'

interface Auth {
  hadUnauthEvent: boolean
  isLoadingUser: boolean
  isLoggedIn: boolean
  loadUserError: Error | null
  onJWTChanged(newJWT: string | null): void
  user: UserV1 | undefined
}

const AuthContext = createContext<Auth | undefined>(undefined)

const UNAUTHORIZED_EVENT_NAME = 'mytovala:unauthorized'

export function dispatchUnauthorizedEvent() {
  const unauthorizedEvent = new Event(UNAUTHORIZED_EVENT_NAME)
  document.dispatchEvent(unauthorizedEvent)
}

function getJWTExpirationDate(decodedJWT: JWTPayload) {
  return new Date(decodedJWT.exp * 1000)
}

const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [hadUnauthEvent, setHadUnauthEvent] = useState(false)
  const [jwt, setJwt] = useState<string | null>(() => {
    const jwt = getCookie('JWT_TOKEN') ?? null
    if (!jwt) {
      return null
    }

    const decodedJWT = jwtDecode<JWTPayload>(jwt)
    const isExpired = isDateBefore(getJWTExpirationDate(decodedJWT), new Date())

    return isExpired ? null : jwt
  })
  const [userID, setUserID] = useState<number | undefined>()

  const {
    data: user,
    error: loadUserError,
    isError: hasLoadUserError,
    isPending: isLoadingUser,
  } = useUserV1({ userID })

  useEffect(() => {
    manageCookieForJWT(jwt)

    if (jwt) {
      const decodedJWT = jwtDecode<JWTPayload>(jwt)

      setUserID(decodedJWT.userId)

      identifyUserFromJWT({ decodedJWT })
      identifyCIOUser(decodedJWT.userId)
    } else {
      setUserID(undefined)
    }
  }, [jwt])

  useEffect(() => {
    function handleUnauthorizedEvent() {
      setHadUnauthEvent(true)
      setJwt(null)
      clearPendingRecommendedMealFilters()
      resetCIOUser()
    }

    document.addEventListener(UNAUTHORIZED_EVENT_NAME, handleUnauthorizedEvent)

    return () => {
      document.removeEventListener(
        UNAUTHORIZED_EVENT_NAME,
        handleUnauthorizedEvent,
      )
    }
  }, [])

  useEffect(() => {
    if (user) {
      identifyUser({ user })
    }
  }, [user])

  return (
    <AuthContext.Provider
      value={{
        hadUnauthEvent,
        isLoadingUser: !!userID && isLoadingUser,
        isLoggedIn: !!jwt,
        loadUserError: hasLoadUserError ? loadUserError : null,
        onJWTChanged: (newJWT: string | null) => {
          manageCookieForJWT(newJWT)
          setJwt(newJWT)

          if (newJWT) {
            setHadUnauthEvent(false)
            // Logging in is the only moment we're applying the user's
            // recommended filters, and the menu is where the data to do it
            // lives, so hand the menu a one-shot flag rather than acting here.
            setPendingRecommendedMealFilters()
          } else {
            // Never let a flag survive into the next session on a shared browser.
            clearPendingRecommendedMealFilters()
            resetCIOUser()
          }
        },
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function manageCookieForJWT(jwt: string | null) {
  const cookiePathAndDomain = getTovalaCookieOptions()

  if (jwt) {
    const decodedJWT = jwtDecode<JWTPayload>(jwt)

    setCookie({
      content: jwt,
      cookieName: 'JWT_TOKEN',
      options: {
        ...cookiePathAndDomain,
        expires: getJWTExpirationDate(decodedJWT),
        sameSite: 'lax',
      },
    })
  } else {
    removeCookie('JWT_TOKEN', cookiePathAndDomain)
  }
}

function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used in an AuthProvider')
  }

  return context
}

export { AuthProvider, useAuth }
